import { useState } from "react";
import type { AdminDeviceBinding, AdminDeviceBindingBody } from "../api/types";
import { formatDateTime } from "../format";
import { useAsync } from "../hooks";
import { useSession } from "../session/SessionContext";
import { ConfirmDialog } from "./ConfirmDialog";
import { ErrorNotice, Loading } from "./Notice";
import { Section } from "./Section";

type Change = "exempt" | "unexempt" | "reset";

const CHANGES: Record<
  Change,
  { title: string; confirm: string; body: AdminDeviceBindingBody; danger: boolean; copy: string }
> = {
  exempt: {
    title: "Exempt from single-device login",
    confirm: "Exempt",
    body: { exempt: true },
    danger: true,
    copy:
      "Any phone signed in to this account will work, with no device check. Use it for the App Review account, shared test accounts and automation, not for members.",
  },
  unexempt: {
    title: "Remove the exemption",
    confirm: "Remove exemption",
    body: { exempt: false },
    danger: false,
    copy: "The device check applies again. The next phone the member opens the app on claims the account if none is bound.",
  },
  reset: {
    title: "Reset the bound phone",
    confirm: "Reset",
    body: { reset: true },
    danger: true,
    copy:
      "The bound phone is signed out on its next request, and whichever phone opens the app next claims the account. Only do this when you are certain who the member is: unlike a recovery, there is no cooldown and no notice to the old phone.",
  },
};

/**
 * Single-device login for one member: which phone holds the account, and the
 * two levers staff have over it (D7). Rendered for admins only and never on
 * their own row; the API refuses both.
 */
export function DeviceBindingSection({ userId }: { userId: string }) {
  const { api } = useSession();
  const binding = useAsync(() => api.deviceBinding(userId), [api, userId]);
  const [shown, setShown] = useState<AdminDeviceBinding | null>(null);
  const [change, setChange] = useState<Change | null>(null);
  const current = shown ?? binding.data;
  const device = current?.activeDevice ?? null;

  return (
    <Section
      title="Device"
      aside={
        current ? (
          <span className={current.exempt ? "chip chip-accent" : "chip chip-muted"}>
            {current.exempt ? "Exempt" : "Single device"}
          </span>
        ) : null
      }
    >
      <div className="panel-body stack">
        {binding.loading && !current ? <Loading what="device" /> : null}
        <ErrorNotice error={binding.error} />
        {current ? (
          <>
            {device ? (
              <p>
                <strong>{device.deviceLabel}</strong>{" "}
                <span className="subtle">
                  ({device.platform === "ios" ? "iOS" : "Android"}), bound{" "}
                  {formatDateTime(device.registeredAt)}
                  {device.lastSeenAt ? `, last seen ${formatDateTime(device.lastSeenAt)}` : ""}
                </span>
              </p>
            ) : (
              <p className="subtle">
                No phone bound. The next phone that opens the app claims the account.
              </p>
            )}
            <div className="row">
              <button
                type="button"
                className="small"
                onClick={() => setChange(current.exempt ? "unexempt" : "exempt")}
              >
                {current.exempt ? "Remove exemption" : "Exempt"}
              </button>
              {device ? (
                <button type="button" className="danger small" onClick={() => setChange("reset")}>
                  Reset phone
                </button>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
      {change ? (
        <ChangeDialog
          userId={userId}
          change={change}
          onDone={(next) => {
            setShown(next);
            setChange(null);
          }}
          onCancel={() => setChange(null)}
        />
      ) : null}
    </Section>
  );
}

function ChangeDialog({
  userId,
  change,
  onDone,
  onCancel,
}: {
  userId: string;
  change: Change;
  onDone: (binding: AdminDeviceBinding) => void;
  onCancel: () => void;
}) {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const spec = CHANGES[change];

  async function apply() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Show what the API wrote, not what was asked for.
      onDone(await api.setDeviceBinding(userId, spec.body));
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      title={spec.title}
      confirmLabel={spec.confirm}
      busyLabel="Saving..."
      busy={busy}
      danger={spec.danger}
      onConfirm={() => void apply()}
      onCancel={onCancel}
    >
      <p>{spec.copy}</p>
      <ErrorNotice error={error} />
    </ConfirmDialog>
  );
}
